import type { TableOfContentsItem } from './tableOfContentsTypes.ts';

const SCROLL_BOUNDARY_TOLERANCE_PX = 1;

export type ResolvedHeading = {
	element: Element;
	headingId: string;
	scrollMarginTop: number;
};

export function resolveHeadings(
	items: TableOfContentsItem[],
	getHeadingElement: (headingId: string) => Element | null | undefined,
): ResolvedHeading[] {
	return items.flatMap((item): ResolvedHeading[] => {
		const element = getHeadingElement(item.headingId);
		if (!element) {
			return [];
		}

		const scrollMarginTop = Number.parseFloat(
			element.ownerDocument.defaultView?.getComputedStyle(element).scrollMarginTop ?? '',
		);
		return [
			{
				element,
				headingId: item.headingId,
				scrollMarginTop: Number.isFinite(scrollMarginTop) ? scrollMarginTop : 0,
			},
		];
	});
}

export function getScrollSelectedHeadingId(
	resolvedHeadings: ResolvedHeading[],
	scrollBoundaryTop: number,
): string | undefined {
	let firstRenderedHeadingId: string | undefined;

	for (let index = resolvedHeadings.length - 1; index >= 0; index--) {
		const { element, headingId, scrollMarginTop } = resolvedHeadings[index];
		const headingRect = element.getBoundingClientRect();
		if (headingRect.width === 0 && headingRect.height === 0) {
			continue;
		}

		firstRenderedHeadingId = headingId;
		if (headingRect.top <= scrollBoundaryTop + scrollMarginTop + SCROLL_BOUNDARY_TOLERANCE_PX) {
			return headingId;
		}
	}

	return firstRenderedHeadingId;
}

export function addRafThrottledScrollListener(
	scrollContainer: Element | Document,
	ownerWindow: Window,
	onScroll: () => void,
): () => void {
	let animationFrameId: number | undefined;
	const scheduleScrollUpdate = () => {
		if (animationFrameId !== undefined) {
			return;
		}

		animationFrameId = ownerWindow.requestAnimationFrame(() => {
			animationFrameId = undefined;
			onScroll();
		});
	};

	scrollContainer.addEventListener('scroll', scheduleScrollUpdate, { passive: true });
	return () => {
		scrollContainer.removeEventListener('scroll', scheduleScrollUpdate);
		if (animationFrameId !== undefined) {
			ownerWindow.cancelAnimationFrame(animationFrameId);
		}
	};
}
